import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { CHART_COLORS, DONUT_COLORS, DONUT_OTHER_COLOR } from '../typings/chart';
import { ChartPointModel }                               from '../typings/intelligence.chart-point';
import { ChartSeriesModel }                              from '../typings/intelligence.chart-series';
import { DonutSegmentModel }                             from '../typings/intelligence.donut-segment';
import { IntelligenceFormatService }                     from '../intelligence.format.service';
import { IntelligenceService }                           from '../intelligence.service';
import { IntelligenceVehicleMakeModel }                  from '../typings/intelligence.vehicle-make';
import { IntelligenceVehiclesModel }                     from '../typings/intelligence.vehicles';
import { IntelligenceVehicleTypeModel }                  from '../typings/intelligence.vehicle-type';
import { TableSortModel }                                from '../typings/intelligence.table-sort';

@Component({
  selector   : 'wor-intelligence-vehicles-tab',
  template: require('./intelligence.vehicles-tab.component.html')
})
export class VehiclesTabComponent implements OnInit, OnDestroy {
  error = false;
  loading = true;

  avgSalePoints : Array<ChartPointModel> = [];
  driveTypeSegments : Array<DonutSegmentModel> = [];
  effectivenessPoints : Array<ChartPointModel> = [];
  fuelTypeSegments : Array<DonutSegmentModel> = [];
  makes : Array<IntelligenceVehicleMakeModel> = [];
  segments : Array<DonutSegmentModel> = [];
  sort : TableSortModel = { direction: 'desc', field: 'revenue_ex_tax' };
  trendPoints : Array<ChartPointModel> = [];

  avgSaleSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 1 ], key: 'avg_sale_ex_tax', label: this.translateService.instant('INTELLIGENCE_SPACE.KPIS.AVG_INVOICE') }
  ];

  effectivenessSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 0 ], key: 'messages_sent', label: this.translateService.instant('INTELLIGENCE_SPACE.VEHICLES.SERVICE_MESSAGES') },
    { color: CHART_COLORS[ 1 ], key: 'services_completed', label: this.translateService.instant('INTELLIGENCE_SPACE.VEHICLES.SERVICES_COMPLETED') }
  ];

  trendSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 1 ], key: 'invoice_count', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.INVOICES') },
    { color: CHART_COLORS[ 0 ], key: 'unique_vehicles', label: this.translateService.instant('INTELLIGENCE_SPACE.VEHICLES.UNIQUE_VEHICLES') }
  ];

  private loadGeneration = 0;
  private subscription : Subscription;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService       : IntelligenceService,
    private translateService         : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  count ( value : number ) : string {
    return this.intelligenceFormatService.count(value);
  }

  grossProfit ( make : IntelligenceVehicleMakeModel ) : number {
    return make.revenue_ex_tax - make.cost_ex_tax;
  }

  isAvgSaleEmpty () : boolean {
    return !this.avgSalePoints.some(point => point.values.avg_sale_ex_tax > 0);
  }

  isDriveTypesEmpty () : boolean {
    return !this.driveTypeSegments.some(segment => segment.value > 0);
  }

  isEffectivenessEmpty () : boolean {
    return !this.effectivenessPoints.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }

  isEmpty () : boolean {
    return !this.makes.length;
  }

  isFuelTypesEmpty () : boolean {
    return !this.fuelTypeSegments.some(segment => segment.value > 0);
  }

  isTrendEmpty () : boolean {
    return !this.trendPoints.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    this.intelligenceService.getVehicles()
    .then(( data : IntelligenceVehiclesModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      this.makes = data.makes || [];

      const top = this.makes.slice(0, DONUT_COLORS.length);

      this.segments = top.map(( make, index ) => ({
        color: DONUT_COLORS[ index ],
        label: make.vehicle_make,
        value: make.revenue_ex_tax
      }));

      if ( this.makes.length > top.length ) {
        this.segments.push({
          color: DONUT_OTHER_COLOR,
          label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'),
          value: this.makes.slice(top.length).reduce(( sum, make ) => sum + make.revenue_ex_tax, 0)
        });
      }

      this.trendPoints = ( data.series || [] ).map(day => ({
        label : this.intelligenceFormatService.date(day.date),
        values: {
          invoice_count  : day.invoice_count,
          unique_vehicles: day.unique_vehicles
        }
      }));

      this.avgSalePoints = ( data.series || [] ).map(day => ({
        label : this.intelligenceFormatService.date(day.date),
        values: { avg_sale_ex_tax: day.avg_sale_ex_tax || 0 }
      }));

      this.effectivenessPoints = ( data.service_effectiveness || [] ).map(day => ({
        label : this.intelligenceFormatService.date(day.date),
        values: {
          messages_sent     : day.messages_sent,
          services_completed: day.services_completed
        }
      }));

      this.fuelTypeSegments = this.buildTypeSegments(data.fuel_types || []);
      this.driveTypeSegments = this.buildTypeSegments(data.drive_types || []);
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  money ( value : number ) : string {
    return this.intelligenceFormatService.money(value);
  }

  sortBy ( field : string ) : void {
    this.sort = {
      direction: this.sort.field === field && this.sort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.makes = this.intelligenceFormatService.sortRows(this.makes, this.sort.field, this.sort.direction);
  }

  private buildTypeSegments ( types : Array<IntelligenceVehicleTypeModel> ) : Array<DonutSegmentModel> {
    const kept = types.slice(0, DONUT_COLORS.length);
    const remainder = types.slice(DONUT_COLORS.length).reduce(( sum, type ) => sum + type.revenue_ex_tax, 0);

    const segments = kept.map(( type, index ) => ({
      color: DONUT_COLORS[ index ],
      label: type.label,
      value: type.revenue_ex_tax
    }));

    if ( remainder > 0 ) {
      segments.push({
        color: DONUT_OTHER_COLOR,
        label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'),
        value: remainder
      });
    }

    return segments;
  }
}
